// Judging a lab result against the clinical lead's rules (docs/plans/phase1-lab.md §2.6,
// §5.3). The rules engine is the shared one in clinical-rules/ (the same one triage uses);
// this module only adds what lab results need on top:
//
// - the rule's subject is an analyte, keyed by its ID;
// - a censored value ("< 0.5", "> 1000") stands for a whole interval of true values, and is
//   flagged only when every value in that interval falls on the same side of a bound.
//   Otherwise it "can't be judged against the range" (`not_applicable`), never "normal";
// - coded results take the interpretation the clinical lead set on the chosen option.
//
// No clinical number lives here; tests use synthetic ones.

import {
  checkWithinLimits,
  selectClinicalRule,
  type ClinicalRuleCore,
  type ClinicalRuleKind,
  type OutsideLimits,
  type RuleSelectionErrorCode,
} from '../clinical-rules/rules.ts';
import type { LabInterpretation } from './flags.ts';
import type { LabComparator, LabNumericValue } from './values.ts';

/** A range rule for one analyte; low and high are scaled in the analyte's decimals. */
export interface LabAnalyteRule extends ClinicalRuleCore {
  readonly analyteId: string;
}

const analyteOf = (rule: LabAnalyteRule) => rule.analyteId;

export interface LabAssessment {
  readonly interpretation: LabInterpretation;
  /** The rule that decided: the critical rule if critical, else the reference rule. */
  readonly rule: LabAnalyteRule | null;
  /** The reference and critical rules in force, for the snapshot and the range column. */
  readonly referenceRule: LabAnalyteRule | null;
  readonly criticalRule: LabAnalyteRule | null;
  /** True when the rule that decided the flag is not yet approved. */
  readonly provisional: boolean;
}

export type LabAssessmentResult =
  | { readonly ok: true; readonly value: LabAssessment }
  | OutsideLimits<LabAnalyteRule>
  | { readonly ok: false; readonly code: RuleSelectionErrorCode };

export interface LabAssessmentInput {
  readonly analyteId: string;
  readonly value: LabNumericValue;
  /** Age in whole days at collection; null when no date of birth is recorded. */
  readonly ageDays: number | null;
  /** The patient's sex code; null when not recorded. */
  readonly sex: string | null;
}

/** The true values a reported number can stand for: bounds with inclusivity. */
interface Interval {
  readonly from: number | null;
  readonly fromIncluded: boolean;
  readonly to: number | null;
  readonly toIncluded: boolean;
}

function intervalOf(scaled: number, comparator: LabComparator | null): Interval {
  switch (comparator) {
    case null:
      return { from: scaled, fromIncluded: true, to: scaled, toIncluded: true };
    case 'lt':
      return { from: null, fromIncluded: false, to: scaled, toIncluded: false };
    case 'le':
      return { from: null, fromIncluded: false, to: scaled, toIncluded: true };
    case 'gt':
      return { from: scaled, fromIncluded: false, to: null, toIncluded: false };
    case 'ge':
      return { from: scaled, fromIncluded: true, to: null, toIncluded: false };
  }
}

/** Every value in the interval is below `low` (a value equal to a bound is inside). */
function whollyBelow(interval: Interval, low: number | null): boolean {
  if (low === null || interval.to === null) return false;
  return interval.toIncluded ? interval.to < low : interval.to <= low;
}

/** Every value in the interval is above `high`. */
function whollyAbove(interval: Interval, high: number | null): boolean {
  if (high === null || interval.from === null) return false;
  return interval.fromIncluded ? interval.from > high : interval.from >= high;
}

/** Every value in the interval lies within [low, high]. */
function whollyWithin(interval: Interval, low: number | null, high: number | null): boolean {
  const aboveLow = low === null || (interval.from !== null && interval.from >= low);
  const belowHigh = high === null || (interval.to !== null && interval.to <= high);
  return aboveLow && belowHigh;
}

/** Might any value in the interval lie beyond the rule's bounds? */
function mightBeOutside(interval: Interval, rule: ClinicalRuleCore): boolean {
  return !whollyWithin(interval, rule.low, rule.high);
}

function judge(
  interval: Interval,
  reference: LabAnalyteRule | null,
  critical: LabAnalyteRule | null,
): { interpretation: LabInterpretation; rule: LabAnalyteRule | null } {
  if (critical !== null) {
    if (whollyBelow(interval, critical.low))
      return { interpretation: 'critical_low', rule: critical };
    if (whollyAbove(interval, critical.high)) {
      return { interpretation: 'critical_high', rule: critical };
    }
    // A censored value that might or might not be critical can't be called either way.
    if (mightBeOutside(interval, critical)) return { interpretation: 'not_applicable', rule: null };
  }
  if (reference === null) return { interpretation: 'no_range', rule: null };
  if (whollyBelow(interval, reference.low)) return { interpretation: 'low', rule: reference };
  if (whollyAbove(interval, reference.high)) return { interpretation: 'high', rule: reference };
  if (whollyWithin(interval, reference.low, reference.high)) {
    return { interpretation: 'normal', rule: reference };
  }
  return { interpretation: 'not_applicable', rule: null };
}

/**
 * The whole judgement for one numeric result: refused when the number is outside the
 * possible limits, otherwise flagged against the critical and reference ranges for the
 * patient's age and sex. An uncensored value gets exactly the triage engine's answer.
 */
export function assessLabNumeric(
  rules: readonly LabAnalyteRule[],
  input: LabAssessmentInput,
): LabAssessmentResult {
  const select = (kind: ClinicalRuleKind) =>
    selectClinicalRule(
      rules,
      { subject: input.analyteId, kind, ageDays: input.ageDays, context: null, sex: input.sex },
      analyteOf,
    );
  const limits = select('possible_limits');
  if (!limits.ok) return limits;
  const within = checkWithinLimits(input.value.scaled, limits.value);
  if (!within.ok) return within;
  const reference = select('reference_range');
  if (!reference.ok) return reference;
  const critical = select('critical_range');
  if (!critical.ok) return critical;

  const judged = judge(
    intervalOf(input.value.scaled, input.value.comparator),
    reference.value,
    critical.value,
  );
  return {
    ok: true,
    value: {
      ...judged,
      referenceRule: reference.value,
      criticalRule: critical.value,
      provisional: judged.rule?.approval === 'provisional',
    },
  };
}

/** What the clinical lead set on a coded answer option (plan §2.1, C14). */
export const LAB_OPTION_INTERPRETATIONS = ['normal', 'abnormal', 'critical', 'none'] as const;
export type LabOptionInterpretation = (typeof LAB_OPTION_INTERPRETATIONS)[number];

export function interpretCodedResult(option: LabOptionInterpretation): LabInterpretation {
  switch (option) {
    case 'normal':
      return 'normal';
    case 'abnormal':
      return 'abnormal';
    case 'critical':
      return 'critical';
    case 'none':
      return 'not_applicable';
  }
}
